import React, { useState, useEffect } from 'react';
import testConfigurationService from '../services/testConfigurationService';
import tenantService from '../services/tenantService';
import {
  Settings,
  Search,
  Plus,
  Eye,
  Users,
  Globe,
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Filter,
  MoreVertical,
  Edit,
  Trash2
} from 'lucide-react';

// Modern Toggle Switch Component
const ToggleSwitch = ({ checked, onChange, disabled = false, size = 'md', label, description }) => {
  const handleClick = () => {
    if (!disabled && onChange) {
      onChange({ target: { checked: !checked } });
    }
  };

  const sizeClasses = {
    sm: { container: 'w-8 h-4', thumb: 'w-3 h-3', translate: 'translate-x-4' },
    md: { container: 'w-11 h-6', thumb: 'w-5 h-5', translate: 'translate-x-5' },
    lg: { container: 'w-14 h-7', thumb: 'w-6 h-6', translate: 'translate-x-7' }
  };

  const sizes = sizeClasses[size];

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className={`
          relative inline-flex items-center
          ${sizes.container}
          rounded-full
          transition-all duration-300 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-offset-2
          ${disabled
            ? 'opacity-40 cursor-not-allowed focus:ring-gray-300'
            : 'cursor-pointer hover:shadow-md'
          }
          ${checked
            ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 focus:ring-emerald-300'
            : 'bg-gradient-to-r from-gray-200 to-gray-300 focus:ring-gray-300'
          }
        `}
        style={{
          boxShadow: checked
            ? '0 4px 12px rgba(16, 185, 129, 0.3)'
            : '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}
      >
        <span
          className={`
            ${sizes.thumb}
            bg-white
            rounded-full
            shadow-lg
            transform transition-all duration-300 ease-in-out
            flex items-center justify-center
            ${checked ? sizes.translate : 'translate-x-0.5'}
          `}
          style={{
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
          }}
        >
          {checked ? (
            <CheckCircle className="w-3 h-3 text-emerald-600" />
          ) : (
            <XCircle className="w-3 h-3 text-gray-400" />
          )}
        </span>
      </button>
      {(label || description) && (
        <div className="flex flex-col">
          {label && <span className="text-sm font-medium text-gray-700">{label}</span>}
          {description && <span className="text-xs text-gray-500">{description}</span>}
        </div>
      )}
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ isActive, isModifiable }) => {
  if (!isModifiable) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
        <Shield className="w-3 h-3" />
        Standard
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
      isActive
        ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
        : 'bg-red-100 text-red-800 border border-red-200'
    }`}>
      {isActive ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
};

// Configuration Card Component
const ConfigurationCard = ({ config, onToggle, onViewDetails, onManageTenants, onEdit, confirmToggle, tenants = [] }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">{config.name}</h3>
              <StatusBadge isActive={config.isActive} isModifiable={config.modifiable} />
            </div>
            <p className="text-sm text-gray-600 mb-3">{config.description}</p>
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                {config.category === 'STANDARD' ? (
                  <>
                    <Shield className="w-3 h-3" />
                    <span className="text-xs">Standard Pattern</span>
                  </>
                ) : (
                  <>
                    <Settings className="w-3 h-3" />
                    <span className="text-xs">Custom Pattern</span>
                  </>
                )}
              </span>
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {(() => {
                  if (!config.tenantActivation) {
                    // If no tenant activation data, show total tenants if globally active, 0 if not
                    return config.isActive ? tenants.length : 0;
                  }
                  // Count tenants that have this configuration enabled
                  const activeTenantCount = Object.values(config.tenantActivation).filter(isActive => isActive).length;
                  return activeTenantCount;
                })()} active tenants
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onViewDetails(config)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="View Details"
            >
              <Eye className="w-4 h-4" />
            </button>
            {config.modifiable && (
              <button
                onClick={() => onEdit(config)}
                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Edit Configuration"
              >
                <Edit className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => onManageTenants(config)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Manage Tenants"
            >
              <Users className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Toggle Section */}
      <div className="p-6 bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-1">System Status</h4>
            <p className="text-xs text-gray-500">
              {config.isActive ? 'Available for test creation' : 'Disabled system-wide'}
            </p>
          </div>
          <ToggleSwitch
            checked={confirmToggle && confirmToggle.config.id === config.id ? confirmToggle.newStatus : config.isActive}
            onChange={(e) => onToggle(config, e.target.checked)}
            disabled={!config.modifiable}
            size="md"
          />
        </div>
        {!config.modifiable && (
          <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-700 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Standard configurations cannot be modified
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const CONFIG_NAME_MAX_LENGTH = 120;

const TestConfigurationsPage = () => {
  const [configurations, setConfigurations] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [error, setError] = useState(null);
  const [confirmToggle, setConfirmToggle] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createConfigJson, setCreateConfigJson] = useState('');
  const [editConfigJson, setEditConfigJson] = useState('');
  const [createError, setCreateError] = useState(null);
  const [editError, setEditError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadConfigurations();
    loadTenants();
  }, []);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading test configurations...');
      const data = await testConfigurationService.getAll();
      console.log('Received configurations:', data);
      setConfigurations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load test configurations:', error);
      setError(`Failed to load test configurations: ${error.message}`);
      setConfigurations([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTenants = async () => {
    try {
      const data = await tenantService.getAllTenants(0, 100); // Get first 100 tenants
      setTenants(data.content || data || []);
    } catch (error) {
      console.error('Failed to load tenants:', error);
      setTenants([]);
    }
  };

  const handleCreateConfiguration = async () => {
    try {
      setCreating(true);
      setCreateError(null);

      // Parse JSON
      const configData = JSON.parse(createConfigJson);

      // Validate required fields
      if (!configData.name || !configData.examType) {
        throw new Error('Name and examType are required fields');
      }

      if (configData.name.trim().length > CONFIG_NAME_MAX_LENGTH) {
        throw new Error(`Configuration name must be ${CONFIG_NAME_MAX_LENGTH} characters or fewer`);
      }

      // Create configuration
      await testConfigurationService.create(configData);

      // Reload configurations
      await loadConfigurations();

      // Close modal and reset form
      setShowCreateModal(false);
      setCreateConfigJson('');
      setCreateError(null);

    } catch (error) {
      console.error('Failed to create configuration:', error);
      setCreateError(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateConfiguration = async () => {
    try {
      setUpdating(true);
      setEditError(null);

      // Parse JSON
      const configData = JSON.parse(editConfigJson);

      // Validate required fields
      if (!configData.name || !configData.examType) {
        throw new Error('Name and examType are required fields');
      }

      if (configData.name.trim().length > CONFIG_NAME_MAX_LENGTH) {
        throw new Error(`Configuration name must be ${CONFIG_NAME_MAX_LENGTH} characters or fewer`);
      }

      // Check if configuration is modifiable
      if (!selectedConfig.modifiable) {
        throw new Error('This is a standard configuration that cannot be modified');
      }

      // Update configuration
      await testConfigurationService.update(selectedConfig.id, configData);

      // Reload configurations
      await loadConfigurations();

      // Close modal and reset form
      setShowEditModal(false);
      setEditConfigJson('');
      setEditError(null);
      setSelectedConfig(null);

    } catch (error) {
      console.error('Failed to update configuration:', error);
      setEditError(error.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleActive = async (config, newStatus) => {
    try {
      console.log('Updating configuration status:', { id: config.id, newStatus });

      // Check if configuration is modifiable
      if (!config.modifiable) {
        setError(`Cannot modify "${config.name}" - this is a standard configuration that cannot be changed.`);
        setConfirmToggle(null);
        return;
      }

      const response = await testConfigurationService.updateStatus(config.id, newStatus);
      console.log('Update response:', response);

      // Create updated tenant activation object - when master toggle changes, all tenants follow
      const updatedTenantActivation = {};
      if (config.tenantActivation) {
        // Set all existing tenant activations to the new master status
        Object.keys(config.tenantActivation).forEach(tenantId => {
          updatedTenantActivation[tenantId] = newStatus;
        });
      }
      // Also set all current tenants to the new status
      tenants.forEach(tenant => {
        updatedTenantActivation[tenant.id] = newStatus;
      });

      // Update the configuration in state directly instead of reloading all
      setConfigurations(prevConfigs =>
        prevConfigs.map(c =>
          c.id === config.id ? {
            ...c,
            isActive: newStatus,
            tenantActivation: updatedTenantActivation
          } : c
        )
      );

      // Update selectedConfig if it's the one being modified
      if (selectedConfig && selectedConfig.id === config.id) {
        setSelectedConfig(prev => ({
          ...prev,
          isActive: newStatus,
          tenantActivation: updatedTenantActivation
        }));
      }

      setConfirmToggle(null);
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error('Failed to update configuration status:', error);
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError(`Failed to update configuration status: ${error.message}`);
      }
      setConfirmToggle(null);
    }
  };

  const handleTenantStatusUpdate = async (configId, tenantId, isActive) => {
    try {
      console.log('Updating tenant status:', { configId, tenantId, isActive });

      // Optimistically update the UI first
      setConfigurations(prevConfigs =>
        prevConfigs.map(config => {
          if (config.id === configId) {
            const updatedTenantActivation = {
              ...config.tenantActivation,
              [tenantId]: isActive
            };
            return { ...config, tenantActivation: updatedTenantActivation };
          }
          return config;
        })
      );

      // Update selectedConfig if it's the one being modified
      if (selectedConfig && selectedConfig.id === configId) {
        setSelectedConfig(prev => ({
          ...prev,
          tenantActivation: {
            ...prev.tenantActivation,
            [tenantId]: isActive
          }
        }));
      }

      const response = await testConfigurationService.updateTenantStatus(configId, tenantId, isActive);
      console.log('Tenant status update response:', response);
      setError(null);
    } catch (error) {
      console.error('Failed to update tenant status:', error);
      // Revert the optimistic update on error
      await loadConfigurations();
      setError(`Failed to update tenant status: ${error.message}`);
    }
  };

  const filteredConfigurations = Array.isArray(configurations) ? configurations.filter(config => {
    // Search filter
    const matchesSearch = config.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.examType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.description?.toLowerCase().includes(searchTerm.toLowerCase());

    // Tenant filter
    let matchesTenant = true;
    if (selectedTenant !== 'all') {
      // Check if the configuration is active for the selected tenant
      if (config.tenantActivation && config.tenantActivation[selectedTenant] !== undefined) {
        matchesTenant = config.tenantActivation[selectedTenant];
      } else {
        // If no specific tenant activation is set, use the global isActive status
        matchesTenant = config.isActive;
      }
    }

    return matchesSearch && matchesTenant;
  }) : [];

  const standardConfigurations = filteredConfigurations.filter(c => c.category === 'STANDARD');
  const customConfigurations = filteredConfigurations.filter(c => c.category === 'CUSTOM');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Test Configurations</h1>
              <p className="text-lg text-gray-600">Manage test patterns and tenant activation settings</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Plus className="w-5 h-5" />
              Create Configuration
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search configurations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={selectedTenant}
                onChange={(e) => setSelectedTenant(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="all">All Tenants</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">Total</span>
            </div>
            <p className="text-2xl font-bold text-blue-900 mt-1">{configurations.length}</p>
          </div>
          <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-900">Standard</span>
            </div>
            <p className="text-2xl font-bold text-amber-900 mt-1">{standardConfigurations.length}</p>
          </div>
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-900">Custom</span>
            </div>
            <p className="text-2xl font-bold text-emerald-900 mt-1">{customConfigurations.length}</p>
          </div>
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-900">Filtered</span>
            </div>
            <p className="text-2xl font-bold text-purple-900 mt-1">{filteredConfigurations.length}</p>
          </div>
        </div>

        {/* Configurations Grid */}
        <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
          {filteredConfigurations.map((config) => (
            <ConfigurationCard
              key={config.id}
              config={config}
              tenants={tenants}
              onToggle={(config, newStatus) => setConfirmToggle({ config, newStatus })}
              onViewDetails={(config) => {
                setSelectedConfig(config);
                setShowDetails(true);
              }}
              onEdit={(config) => {
                setSelectedConfig(config);
                setEditConfigJson(JSON.stringify(config, null, 2));
                setEditError(null);
                setShowEditModal(true);
              }}
              onManageTenants={(config) => {
                setSelectedConfig(config);
                setShowTenantModal(true);
              }}
              confirmToggle={confirmToggle}
            />
          ))}
        </div>

        {filteredConfigurations.length === 0 && (
          <div className="text-center py-12">
            <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No configurations found</h3>
            <p className="text-gray-500 mb-6">Try adjusting your search or filter criteria</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Configuration
            </button>
          </div>
        )}

      {/* Configuration Details Modal */}
      {showDetails && selectedConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold">{selectedConfig.name}</h2>
                <p className="text-gray-600">{selectedConfig.description}</p>
              </div>
              <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            {/* Basic Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="text-sm font-medium text-gray-700">Duration</label>
                <p className="text-sm text-gray-600">{selectedConfig.duration} minutes</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Total Questions</label>
                <p className="text-sm text-gray-600">{selectedConfig.totalQuestions}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Exam Type</label>
                <p className="text-sm text-gray-600">{selectedConfig.examType}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Category</label>
                <p className="text-sm text-gray-600">{selectedConfig.category}</p>
              </div>
            </div>

            {/* Subject Configurations */}
            {selectedConfig.subjectConfigurations && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Subject Configuration</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Subject</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Questions</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Time (min)</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Weight %</th>
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Difficulty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedConfig.subjectConfigurations.map((subject, index) => (
                        <tr key={index} className="border-t border-gray-200">
                          <td className="px-4 py-2">
                            <div>
                              <div className="font-medium">{subject.subjectName}</div>
                              <div className="text-sm text-gray-500">{subject.subjectCode}</div>
                            </div>
                          </td>
                          <td className="px-4 py-2">{subject.questionCount}</td>
                          <td className="px-4 py-2">{subject.timeAllocation}</td>
                          <td className="px-4 py-2">{subject.weightPercentage}%</td>
                          <td className="px-4 py-2">
                            <div className="text-xs">
                              <div>Easy: {subject.difficultyDistribution.easyPercentage}%</div>
                              <div>Medium: {subject.difficultyDistribution.mediumPercentage}%</div>
                              <div>Hard: {subject.difficultyDistribution.hardPercentage}%</div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Marking Scheme */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Marking Scheme</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Correct Answer</label>
                  <p className="text-sm text-gray-600">+{selectedConfig.markingScheme.correctAnswerMarks}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Wrong Answer</label>
                  <p className="text-sm text-gray-600">{selectedConfig.markingScheme.wrongAnswerMarks}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Unanswered</label>
                  <p className="text-sm text-gray-600">{selectedConfig.markingScheme.unansweredMarks}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Negative Marking</label>
                  <p className="text-sm text-gray-600">
                    {selectedConfig.markingScheme.hasNegativeMarking ? 'Yes' : 'No'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tenant Management Modal */}
      {showTenantModal && selectedConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">Tenant Management</h2>
                  <p className="text-blue-100">{selectedConfig.name}</p>
                </div>
                <button
                  onClick={() => setShowTenantModal(false)}
                  className="text-blue-100 hover:text-white p-2 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Global System Status */}
              <div className="mb-6">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-600 rounded-lg">
                        <Globe className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-blue-900">Global System Status</h3>
                        <p className="text-sm text-blue-700">Master switch - controls system-wide availability</p>
                      </div>
                    </div>
                    <ToggleSwitch
                      checked={confirmToggle && confirmToggle.config.id === selectedConfig.id ? confirmToggle.newStatus : selectedConfig.isActive}
                      onChange={(e) => setConfirmToggle({ config: selectedConfig, newStatus: e.target.checked })}
                      disabled={!selectedConfig.modifiable}
                      size="lg"
                      label={selectedConfig.isActive ? 'Enabled' : 'Disabled'}
                    />
                  </div>
                </div>
              </div>

              {/* Individual Tenant Controls */}
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2 rounded-lg ${selectedConfig.isActive ? 'bg-emerald-600' : 'bg-gray-400'}`}>
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${selectedConfig.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                      Individual Tenant Controls
                    </h3>
                    <p className={`text-sm ${selectedConfig.isActive ? 'text-gray-600' : 'text-gray-400'}`}>
                      {selectedConfig.isActive
                        ? 'Fine-grained control per organization'
                        : 'Enable global system status to access tenant controls'
                      }
                    </p>
                  </div>
                </div>

                {!selectedConfig.isActive && (
                  <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <p className="text-sm text-amber-700 font-medium">
                        Individual tenant controls are disabled when the global system is turned off
                      </p>
                    </div>
                  </div>
                )}

                <div className={`grid gap-3 max-h-96 overflow-y-auto ${!selectedConfig.isActive ? 'opacity-50 pointer-events-none' : ''}`}>
                {tenants.map((tenant) => {
                  const isActiveForTenant = selectedConfig.tenantActivation
                    ? selectedConfig.tenantActivation[tenant.id] !== undefined
                      ? selectedConfig.tenantActivation[tenant.id]
                      : selectedConfig.isActive
                    : selectedConfig.isActive;

                  return (
                    <div key={tenant.id} className={`bg-white border border-gray-200 rounded-xl p-4 transition-all duration-200 ${selectedConfig.isActive ? 'hover:shadow-md' : ''}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${isActiveForTenant && selectedConfig.isActive ? 'bg-emerald-500' : 'bg-gray-300'}`}></div>
                          <div>
                            <div className={`font-medium ${selectedConfig.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                              {tenant.name}
                            </div>
                            <div className={`text-sm ${selectedConfig.isActive ? 'text-gray-500' : 'text-gray-400'}`}>
                              {tenant.code}
                            </div>
                          </div>
                        </div>
                        <ToggleSwitch
                          checked={isActiveForTenant && selectedConfig.isActive}
                          onChange={(e) => handleTenantStatusUpdate(selectedConfig.id, tenant.id, e.target.checked)}
                          disabled={!selectedConfig.isActive || !selectedConfig.modifiable}
                          size="md"
                          label={isActiveForTenant && selectedConfig.isActive ? 'Active' : 'Inactive'}
                        />
                      </div>
                    </div>
                  );
                })}

                {tenants.length === 0 && (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No tenants available</p>
                  </div>
                )}
                </div>
            </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowTenantModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmToggle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-2">
              {confirmToggle.newStatus ? 'Activate' : 'Deactivate'} Configuration
            </h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to {confirmToggle.newStatus ? 'activate' : 'deactivate'} the
              configuration "{confirmToggle.config.name}"?
            </p>
            {!confirmToggle.config.modifiable && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm">
                <strong>Error:</strong> This is a standard configuration that cannot be modified.
              </div>
            )}
            {confirmToggle.config.modifiable && !confirmToggle.newStatus && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                <strong>Warning:</strong> Deactivating this configuration will make it unavailable
                for creating new tests in the admin portal.
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmToggle(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleToggleActive(confirmToggle.config, confirmToggle.newStatus)}
                disabled={!confirmToggle.config.modifiable}
                className={`px-4 py-2 rounded transition-colors ${
                  confirmToggle.config.modifiable
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {confirmToggle.newStatus ? 'Activate' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Configuration Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl w-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">Create Test Configuration</h2>
                  <p className="text-blue-100">Define a new test pattern for your organization</p>
                </div>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateConfigJson('');
                    setCreateError(null);
                  }}
                  className="text-blue-100 hover:text-white p-2 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Configuration JSON</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Enter the test configuration in JSON format. This will create a Custom configuration that can be modified.
                </p>
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    Keep <strong>name</strong> within {CONFIG_NAME_MAX_LENGTH} characters so admins can create clear student-facing test titles from it.
                  </p>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">Example Configuration:</h4>
                  <pre className="text-xs text-gray-700 overflow-x-auto">
{`{
  "name": "JEE Main Practice Test",
  "examType": "JEE_MAIN",
  "description": "Standard JEE Main pattern with Physics, Chemistry, and Mathematics",
  "category": "CUSTOM",
  "modifiable": true,
  "totalQuestions": 90,
  "totalDuration": 180,
  "subjectConfigurations": [
    {
      "subjectName": "Physics",
      "subjectCode": "PHY",
      "questionCount": 30,
      "timeAllocation": 60,
      "weightPercentage": 33.33,
      "difficultyDistribution": {
        "easyPercentage": 30,
        "mediumPercentage": 50,
        "hardPercentage": 20
      }
    }
  ]
}`}
                  </pre>
                </div>

                <textarea
                  value={createConfigJson}
                  onChange={(e) => setCreateConfigJson(e.target.value)}
                  placeholder="Enter configuration JSON here..."
                  className="w-full h-64 p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />

                {createError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{createError}</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateConfigJson('');
                    setCreateError(null);
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateConfiguration}
                  disabled={creating || !createConfigJson.trim()}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {creating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      Create Configuration
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Configuration Modal */}
      {showEditModal && selectedConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl w-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">Edit Test Configuration</h2>
                  <p className="text-green-100">Modify the test pattern configuration</p>
                </div>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditConfigJson('');
                    setEditError(null);
                    setSelectedConfig(null);
                  }}
                  className="text-green-100 hover:text-white p-2 rounded-lg hover:bg-green-600 transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Configuration JSON</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Edit the test configuration in JSON format. Only modifiable configurations can be edited.
                </p>
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    Keep <strong>name</strong> within {CONFIG_NAME_MAX_LENGTH} characters so the downstream admin test title stays readable for students.
                  </p>
                </div>

                <textarea
                  value={editConfigJson}
                  onChange={(e) => setEditConfigJson(e.target.value)}
                  placeholder="Enter configuration JSON here..."
                  className="w-full h-64 p-4 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />

                {editError && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{editError}</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditConfigJson('');
                    setEditError(null);
                    setSelectedConfig(null);
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateConfiguration}
                  disabled={updating || !editConfigJson.trim()}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {updating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <Edit className="w-4 h-4" />
                      Update Configuration
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default TestConfigurationsPage;
